import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingDown } from 'lucide-react';
import { useFunnelLossAnalysis, type FunnelFilters } from '@/hooks/useFunnelMetrics';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from 'recharts';

const STAGE_LABELS: Record<string, string> = {
  new: 'Novo',
  contact: 'Contato',
  scheduled: 'Reunião Agendada',
  proposal: 'Proposta',
  negotiation: 'Negociação',
};

const STAGE_COLORS: Record<string, string> = {
  new: 'hsl(var(--chart-1))',
  contact: 'hsl(var(--chart-2))',
  scheduled: 'hsl(var(--chart-3))',
  proposal: 'hsl(var(--chart-4))',
  negotiation: 'hsl(var(--chart-5))',
};

interface Props {
  filters: FunnelFilters;
}

export default function FunnelLossAnalysis({ filters }: Props) {
  const { data, isLoading } = useFunnelLossAnalysis(filters);

  const chartData = (data || []).map(row => ({
    ...row,
    label: STAGE_LABELS[row.lost_from_stage] || row.lost_from_stage,
    color: STAGE_COLORS[row.lost_from_stage] || 'hsl(var(--muted))',
  }));

  const totalLost = chartData.reduce((sum, row) => sum + row.total_lost, 0);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingDown className="h-5 w-5 text-red-500" />
            Onde Perdemos Deals
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-red-500" />
          Onde Perdemos Deals
          {totalLost > 0 && (
            <span className="text-sm font-normal text-muted-foreground">
              ({totalLost} total)
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {chartData.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">Sem dados de perda para o período</p>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} layout="vertical" margin={{ left: 80, right: 50 }}>
              <XAxis type="number" hide />
              <YAxis 
                type="category" 
                dataKey="label" 
                tick={{ fontSize: 12 }} 
                axisLine={false}
                tickLine={false}
              />
              <Bar 
                dataKey="total_lost" 
                radius={[0, 4, 4, 0]}
                maxBarSize={30}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                <LabelList 
                  dataKey="percent_of_losses" 
                  position="right" 
                  formatter={(value: number) => `${value}%`}
                  style={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}
